import React, { useState, useEffect } from 'react';
import { PortfolioPosition, PortfolioSettings, PerformanceSnapshot } from '../../types';
import { profileService } from '../../services/profileService';
import alphaVantageService from '../../services/alphaVantageService';

interface PortfolioManagerProps {
  onViewStock?: (ticker: string) => void;
}

const PortfolioManager: React.FC<PortfolioManagerProps> = ({ onViewStock }) => {
  const [settings, setSettings] = useState<PortfolioSettings | null>(null);
  const [positions, setPositions] = useState<PortfolioPosition[]>([]);
  const [snapshots, setSnapshots] = useState<PerformanceSnapshot[]>([]);
  const [currentPrices, setCurrentPrices] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Buy form state
  const [showBuyForm, setShowBuyForm] = useState<boolean>(false);
  const [buyTicker, setBuyTicker] = useState<string>('');
  const [buyShares, setBuyShares] = useState<string>('');
  const [buyPrice, setBuyPrice] = useState<string>('');
  const [buyError, setBuyError] = useState<string | null>(null);
  
  // Sell form state
  const [showSellForm, setShowSellForm] = useState<boolean>(false);
  const [selectedPosition, setSelectedPosition] = useState<PortfolioPosition | null>(null);
  const [sellPrice, setSellPrice] = useState<string>('');
  const [sellError, setSellError] = useState<string | null>(null);
  
  // Computed portfolio summary
  const [portfolioSummary, setPortfolioSummary] = useState({
    totalValue: 0,
    cashBalance: 0,
    investedValue: 0,
    totalGainLoss: 0,
    percentReturn: 0
  });
  
  // Fetch portfolio data
  const fetchPortfolioData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch portfolio settings, positions, and performance history
      const [settingsData, positionsData, snapshotsData] = await Promise.all([
        profileService.getPortfolioSettings(),
        profileService.getPositions(),
        profileService.getPerformanceSnapshots(30)
      ]);
      
      setSettings(settingsData);
      setPositions(positionsData);
      setSnapshots(snapshotsData);
      
      // Fetch current prices for all positions
      if (positionsData.length > 0) {
        const tickers = positionsData.map(position => position.ticker);
        const prices = await alphaVantageService.getBatchPrices(tickers);
        setCurrentPrices(prices);
        
        // Calculate portfolio summary
        const totalInvested = positionsData.reduce(
          (sum, position) => sum + position.shares * position.entry_price, 
          0
        );
        
        const currentValue = positionsData.reduce(
          (sum, position) => {
            const currentPrice = prices[position.ticker] || position.entry_price;
            return sum + position.shares * currentPrice;
          }, 
          0
        );
        
        const totalValue = currentValue + (settingsData?.current_cash || 0);
        const gainLoss = currentValue - totalInvested;
        const percentReturn = totalInvested > 0 
          ? (gainLoss / totalInvested) * 100 
          : 0;
        
        setPortfolioSummary({
          totalValue,
          cashBalance: settingsData?.current_cash || 0,
          investedValue: totalInvested,
          totalGainLoss: gainLoss,
          percentReturn
        });
        
        // Take a snapshot of current portfolio value
        await profileService.takePerformanceSnapshot(prices);
      }
    } catch (err) {
      console.error('Error fetching portfolio data:', err);
      setError('Failed to load portfolio data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Load portfolio data on component mount
  useEffect(() => {
    fetchPortfolioData();
    
    // Refresh portfolio data every 5 minutes
    const interval = setInterval(() => {
      fetchPortfolioData();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Handle buy stock submission
  const handleBuySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBuyError(null);
    
    if (!buyTicker || !buyShares || !buyPrice) {
      setBuyError('Please fill in all fields');
      return;
    }
    
    const shares = parseFloat(buyShares);
    const price = parseFloat(buyPrice);
    
    if (isNaN(shares) || shares <= 0) {
      setBuyError('Please enter a valid number of shares');
      return;
    }
    
    if (isNaN(price) || price <= 0) {
      setBuyError('Please enter a valid price');
      return;
    }
    
    const totalCost = shares * price;
    
    if (settings && totalCost > settings.current_cash) {
      setBuyError(`Insufficient funds. Available: $${settings.current_cash.toFixed(2)}`);
      return;
    }
    
    try {
      setLoading(true);
      
      // Execute buy order
      await profileService.buyPosition(
        buyTicker.toUpperCase(),
        shares,
        price
      );
      
      // Reset form
      setBuyTicker('');
      setBuyShares('');
      setBuyPrice('');
      setShowBuyForm(false);
      
      // Refresh portfolio data
      await fetchPortfolioData();
    } catch (err) {
      console.error('Error buying position:', err);
      setBuyError('Failed to execute buy order. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle sell stock submission
  const handleSellSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSellError(null);
    
    if (!selectedPosition || !sellPrice) {
      setSellError('Please fill in all fields');
      return;
    }
    
    const price = parseFloat(sellPrice);
    
    if (isNaN(price) || price <= 0) {
      setSellError('Please enter a valid price');
      return;
    }
    
    try {
      setLoading(true);
      
      // Execute sell order
      await profileService.sellPosition(
        selectedPosition.id,
        price
      );
      
      // Reset form
      setSelectedPosition(null);
      setSellPrice('');
      setShowSellForm(false);
      
      // Refresh portfolio data
      await fetchPortfolioData();
    } catch (err) {
      console.error('Error selling position:', err);
      setSellError('Failed to execute sell order. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle lookup current price
  const handleLookupPrice = async (ticker: string) => {
    try {
      const price = await alphaVantageService.getCurrentPrice(ticker);
      setBuyPrice(price.toString());
    } catch (err) {
      console.error('Error fetching price:', err);
      setBuyError('Failed to fetch current price. Please enter manually.');
    }
  };
  
  // Reset portfolio
  const handleResetPortfolio = async () => {
    if (!window.confirm('Are you sure you want to reset your portfolio? This will close all positions and reset your cash balance.')) {
      return;
    }
    
    try {
      setLoading(true);
      await profileService.resetPortfolio();
      await fetchPortfolioData();
    } catch (err) {
      console.error('Error resetting portfolio:', err);
      setError('Failed to reset portfolio. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate position profit/loss
  const calculatePositionPnL = (position: PortfolioPosition): number => {
    const currentPrice = currentPrices[position.ticker] || position.entry_price;
    return (currentPrice - position.entry_price) * position.shares;
  };
  
  // Calculate position profit/loss percentage
  const calculatePositionPnLPercent = (position: PortfolioPosition): number => {
    const currentPrice = currentPrices[position.ticker] || position.entry_price;
    return ((currentPrice - position.entry_price) / position.entry_price) * 100;
  };
  
  if (loading && !positions.length && !settings) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      {/* Portfolio Summary */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Portfolio Summary</h2>
          <button
            onClick={() => setShowBuyForm(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Buy Stock
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500 uppercase">Total Value</h3>
            <p className="text-2xl font-bold text-gray-900">${portfolioSummary.totalValue.toFixed(2)}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500 uppercase">Cash Balance</h3>
            <p className="text-2xl font-bold text-gray-900">${portfolioSummary.cashBalance.toFixed(2)}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500 uppercase">Total Gain/Loss</h3>
            <p className={`text-2xl font-bold ${portfolioSummary.totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${portfolioSummary.totalGainLoss.toFixed(2)}
              <span className="text-sm ml-1">
                ({portfolioSummary.percentReturn.toFixed(2)}%)
              </span>
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500 uppercase">Positions</h3>
            <p className="text-2xl font-bold text-gray-900">{positions.length}</p>
          </div>
        </div>
        
        <div className="mt-4 text-right">
          <button
            onClick={handleResetPortfolio}
            className="text-sm text-red-600 hover:text-red-800"
          >
            Reset Portfolio
          </button>
        </div>
      </div>
      
      {/* Positions Table */}
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Open Positions</h3>
        
        {positions.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            You don't have any open positions. Use the Buy Stock button to add positions to your portfolio.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ticker
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shares
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entry Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Market Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gain/Loss
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {positions.map((position) => {
                  const currentPrice = currentPrices[position.ticker] || position.entry_price;
                  const marketValue = position.shares * currentPrice;
                  const pnl = calculatePositionPnL(position);
                  const pnlPercent = calculatePositionPnLPercent(position);
                  
                  return (
                    <tr key={position.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div 
                          className="text-sm font-medium text-blue-600 cursor-pointer hover:underline"
                          onClick={() => onViewStock && onViewStock(position.ticker)}
                        >
                          {position.ticker}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {position.shares.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${position.entry_price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${currentPrice.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${marketValue.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${pnl.toFixed(2)} ({pnlPercent.toFixed(2)}%)
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedPosition(position);
                            setSellPrice(currentPrice.toString());
                            setShowSellForm(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Sell
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Performance History */}
      {snapshots.length > 0 && (
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance History</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Portfolio Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cash Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invested Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {snapshots.map((snapshot) => (
                  <tr key={snapshot.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(snapshot.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${snapshot.portfolio_value.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${snapshot.cash_balance.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${(snapshot.portfolio_value - snapshot.cash_balance).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Buy Stock Modal */}
      {showBuyForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Buy Stock</h3>
            
            {buyError && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{buyError}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleBuySubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ticker Symbol
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={buyTicker}
                    onChange={(e) => setBuyTicker(e.target.value.toUpperCase())}
                    className="flex-1 block w-full border-gray-300 rounded-l-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
                    placeholder="e.g., AAPL"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => handleLookupPrice(buyTicker)}
                    className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    Get Price
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Shares
                </label>
                <input
                  type="number"
                  value={buyShares}
                  onChange={(e) => setBuyShares(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
                  placeholder="e.g., 10"
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price per Share ($)
                </label>
                <input
                  type="number"
                  value={buyPrice}
                  onChange={(e) => setBuyPrice(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
                  placeholder="e.g., 150.00"
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>
              
              {settings && (
                <div className="mb-4 text-sm text-gray-600">
                  Available Cash: ${settings.current_cash.toFixed(2)}
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowBuyForm(false)}
                  className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Buy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Sell Stock Modal */}
      {showSellForm && selectedPosition && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Sell {selectedPosition.ticker}
            </h3>
            
            {sellError && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{sellError}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSellSubmit}>
              <div className="mb-4">
                <div className="text-sm text-gray-600 mb-4">
                  <p>Shares: {selectedPosition.shares.toFixed(2)}</p>
                  <p>Entry Price: ${selectedPosition.entry_price.toFixed(2)}</p>
                  <p>Current Value: ${(selectedPosition.shares * (currentPrices[selectedPosition.ticker] || selectedPosition.entry_price)).toFixed(2)}</p>
                </div>
                
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sell Price per Share ($)
                </label>
                <input
                  type="number"
                  value={sellPrice}
                  onChange={(e) => setSellPrice(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
                  placeholder="e.g., 150.00"
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>
              
              {sellPrice && (
                <div className="mb-4 text-sm">
                  <p className="text-gray-600">
                    Total Proceeds: ${(selectedPosition.shares * parseFloat(sellPrice || '0')).toFixed(2)}
                  </p>
                  
                  {!isNaN(parseFloat(sellPrice)) && (
                    <p className={`font-medium ${parseFloat(sellPrice) >= selectedPosition.entry_price ? 'text-green-600' : 'text-red-600'}`}>
                      Profit/Loss: ${((parseFloat(sellPrice) - selectedPosition.entry_price) * selectedPosition.shares).toFixed(2)}
                      ({(((parseFloat(sellPrice) - selectedPosition.entry_price) / selectedPosition.entry_price) * 100).toFixed(2)}%)
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowSellForm(false);
                    setSelectedPosition(null);
                    setSellPrice('');
                  }}
                  className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Sell'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioManager; 